import React from "react";

import { useTranslation } from "react-i18next";

import { Button, Divider, Empty, Form, Input, Modal, Select, Spin } from "antd";

import { useDispatch, useSelector } from "react-redux";

import slice from "./reducer";
import editContractorDrawerSlice from "../EditContractorDrawer/reducer";
import saga from "./saga";

import { makeSelectCreatedContractor, makeSelectLoading, makeSelectOpen } from "./selectors";

import useInjectSaga from "../../utils/useInjectSaga";
import { makeSelectClients, makeSelectUserInfo } from "../../containers/App/selectors";
import BlackListedCompanyName from "../../components/BlackListedCompanyName";

export type CreateLeadFormInput = {
  name?: string;
  clientId?: number;
  companyId: number;
};

const CreateLeadModal = React.memo(() => {
  const { t } = useTranslation("translations");

  useInjectSaga("createLeadModal", saga);

  const [form] = Form.useForm();

  const dispatch = useDispatch();

  const userInfo = useSelector(makeSelectUserInfo());

  const open = useSelector(makeSelectOpen);
  const loading = useSelector(makeSelectLoading);
  const createdContractor = useSelector(makeSelectCreatedContractor);

  const clients = useSelector(makeSelectClients());

  const handleClose = React.useCallback(() => {
    dispatch(slice.actions.closeModal());
  }, [dispatch]);

  const handleFinish = React.useCallback(
    (model: CreateLeadFormInput) => {
      dispatch(slice.actions.createLead(model));
    },
    [dispatch],
  );

  const handleOpenCreateContractorModal = React.useCallback(() => {
    dispatch(editContractorDrawerSlice.actions.openDrawer());
  }, [dispatch]);

  React.useEffect(() => {
    if (createdContractor) {
      form.setFieldValue("clientId", createdContractor.id);
    }
  }, [form, createdContractor]);

  return (
    <Modal open={open} onCancel={handleClose} footer={null} destroyOnHidden title={t("new_lead")}>
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            companyId: userInfo.company.id,
          }}
          onFinish={handleFinish}
        >
          <Form.Item name="companyId" noStyle>
            <Input hidden />
          </Form.Item>

          <Form.Item name="name" label={t("name")}>
            <Input />
          </Form.Item>

          <Form.Item name="clientId" label={t("client")}>
            <Select
              options={clients.map((x) => ({
                label: x.isBlackList ? <BlackListedCompanyName name={x.name} /> : x.name,
                value: x.id,
                name: x.name,
              }))}
              allowClear
              showSearch={{ filterOption: (input, option) => (option?.name ?? "").toLowerCase().includes(input.toLowerCase()) }}
              notFoundContent={
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}>
                  <Button type="primary" onClick={handleOpenCreateContractorModal}>
                    {t("add_contractor")}
                  </Button>
                </Empty>
              }
            />
          </Form.Item>

          <Divider />

          <Button htmlType="submit" type="primary">
            {t("create")}
          </Button>
        </Form>
      </Spin>
    </Modal>
  );
});

export default CreateLeadModal;
